import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemText from '@mui/material/ListItemText'
import Avatar from '@mui/material/Avatar'
import IconButton from '@mui/material/IconButton'
import DeleteIcon from '@mui/icons-material/Delete'
import Divider from '@mui/material/Divider'
import Tooltip from '@mui/material/Tooltip'
import EditIcon from '@mui/icons-material/Edit'
import Box from '@mui/material/Box'
import { ListItemButton, colors } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { interpolateRouteUtil, stringAvatar } from '@assembly/utils'
import { RoutePaths } from '@assembly/constants'
import { RoleLabelMapping, TeamMember } from '@assembly/pages/Teams/types'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import PersonRemoveIcon from '@mui/icons-material/PersonRemove'
import React from 'react'

interface TeamMembersListProps {
  teamMembers: TeamMember[]
  onClickDelete: (teamMember: TeamMember, index: number) => void
  onClickEdit: (teamMember: TeamMember, index: number) => void
  onClickRemoveFromTeam: (teamMember: TeamMember, index: number) => void
}

export default function TeamMembersList(props: TeamMembersListProps) {
  const navigate = useNavigate()
  const params = useParams()
  const [tooltipStates, setTooltipStates] = React.useState<Record<string, string>>({})

  const handleClickTeamMember = (id: string) => {
    navigate(
      interpolateRouteUtil(RoutePaths.TeamMember, {
        orgId: params.orgId || '',
        teamId: params.teamId,
        teamMemberId: id,
      })
    )
  }

  const copyToClipboard = (id: string, text: string) => {
    navigator.clipboard.writeText(text)
    setTooltipStates(prev => ({ ...prev, [id]: 'Copied!' }))
    setTimeout(() => {
      setTooltipStates(prev => ({ ...prev, [id]: 'Copy ID' }))
    }, 2000)
  }

  return (
    <List>
      {props.teamMembers.map((teamMember, index) => (
        <div key={index}>
          <ListItem
            sx={{ padding: 0 }}
            secondaryAction={
              <Box display="flex" gap={3}>
                <Tooltip
                  title={tooltipStates[teamMember.id] || 'Copy ID'}
                  sx={{ marginLeft: 1, color: colors.grey[800] }}
                >
                  <IconButton onClick={() => copyToClipboard(teamMember.id, teamMember.id)}>
                    <ContentCopyIcon sx={{ width: 20, height: 20 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit">
                  <IconButton
                    onClick={() => props.onClickEdit(teamMember, index)}
                    edge="end"
                    sx={{ color: colors.grey[800] }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove from Team">
                  <IconButton
                    onClick={() => props.onClickRemoveFromTeam(teamMember, index)}
                    edge="end"
                    sx={{ color: colors.orange[800] }}
                  >
                    <PersonRemoveIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    onClick={() => props.onClickDelete(teamMember, index)}
                    edge="end"
                    sx={{ color: colors.grey[800] }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            }
          >
            <ListItemButton
              onClick={() => handleClickTeamMember(teamMember.id)}
            >
              <ListItemAvatar>
                <Avatar
                  {...stringAvatar(teamMember.name || teamMember.email || '')}
                />
              </ListItemAvatar>
              <ListItemText
                primary={teamMember.name || teamMember.email || ''}
                secondary={RoleLabelMapping[teamMember.role] || ''}
              />
            </ListItemButton>
          </ListItem>
          <Divider />
        </div>
      ))}
    </List>
  )
}
